export interface SortPreference {
  orderBy: string;
  orderDirection: 'asc' | 'desc';
}

/**
 * Read a page-specific sort preference from localStorage.
 * Returns `defaultSort` when the key is missing or the stored value is invalid.
 */
export function getSortPreference(
  key: string,
  defaultSort: SortPreference,
  allowedOrderBy?: readonly string[],
): SortPreference {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaultSort;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'orderBy' in parsed &&
      'orderDirection' in parsed &&
      typeof (parsed as SortPreference).orderBy === 'string' &&
      ((parsed as SortPreference).orderDirection === 'asc' ||
        (parsed as SortPreference).orderDirection === 'desc')
    ) {
      const pref = parsed as SortPreference;
      if (allowedOrderBy && !allowedOrderBy.includes(pref.orderBy)) {
        return defaultSort;
      }
      return pref;
    }
    return defaultSort;
  } catch {
    return defaultSort;
  }
}

/**
 * Persist a page-specific sort preference to localStorage.
 */
export function setSortPreference(key: string, sort: SortPreference): void {
  try {
    localStorage.setItem(key, JSON.stringify(sort));
  } catch {
    // Silently ignore quota or security errors
  }
}
